import { useQuery } from '@tanstack/react-query'
import { apiFetch, getCallerAgentId, hasLiveApi } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import {
  agentNodeToAccount,
  allocatedCreditFromTree,
  type AgentNode,
} from '@/api/map/agent'
import * as api from '@/data/api'
import type { Account } from '@/data/types'
import { parseCurrencyType } from '@/lib/currency'
import { remainingCredit, type ParentTerms } from '@/features/create/summary'
import { keys } from '@/api/hooks/shared/keys'
import { paiseToDisplay } from '@/lib/currency'

/** Create-wizard parent: Account plus Backend-UI remaining credit line. */
export type CreateParent = Account & ParentTerms

function withAvailableCredit(account: Account, allocated: number, remainingFromApi?: number): CreateParent {
  return {
    ...account,
    availableCredit:
      remainingFromApi != null && Number.isFinite(remainingFromApi)
        ? Math.max(0, remainingFromApi)
        : remainingCredit(account.creditLimit, allocated),
  }
}

async function loadCreateParentFixture(): Promise<CreateParent> {
  const session = await api.getSession()
  const account = await api.getAccount(session.accountId)
  if (!account) throw new Error('Your account could not be loaded')
  const downline = await api.listDownline({
    rootId: account.id,
    directOnly: true,
    pageSize: 500,
  })
  const allocated = downline.rows.reduce((sum, row) => sum + row.creditLimit, 0)
  return withAvailableCredit(account, allocated)
}

/**
 * Parent for the Create wizard.
 * Live Keycloak → hierarchy; unreachable hierarchy → fixture parent so the
 * form shell stays visible (No data / limits from fixture). Demo → fixtures.
 *
 * availableCredit = Lab freeCredit (hierarchy remainingToAssign):
 * creditLimit − distributed + min(0, balanceUp). Wins do not raise it.
 */
async function loadCreateParent(): Promise<CreateParent> {
  if (!hasLiveApi()) return loadCreateParentFixture()
  try {
    const id = getCallerAgentId()!
    const [node, tree] = await Promise.all([
      apiFetch<AgentNode>(`/v1/agents/${id}`),
      apiFetch<AgentNode[]>(`/v1/agents/${id}/tree`),
    ])
    const account = agentNodeToAccount(node)
    const allocated = allocatedCreditFromTree(
      id,
      tree,
      parseCurrencyType(node.currencyType),
    )
    const currencyType = parseCurrencyType(node.currencyType)
    const remainingFromApi =
      node.remainingToAssign != null && String(node.remainingToAssign).trim() !== ''
        ? paiseToDisplay(node.remainingToAssign, currencyType)
        : undefined
    return withAvailableCredit(account, allocated, remainingFromApi)
  } catch (err) {
    if (isUnreachableApiError(err)) return loadCreateParentFixture()
    throw err
  }
}

export function useCreateParent() {
  return useQuery({
    queryKey: keys.createParent,
    queryFn: loadCreateParent,
    staleTime: 30_000,
    retry: 1,
  })
}
